import { ReactElement, useEffect, useRef, useState } from 'react';
import NoodleAI from '@components/Icons/NoodleAI';
import { m } from 'framer-motion';
import DOMPurify from 'isomorphic-dompurify';
import s from './Assistant.module.scss';

type AssistantProps = {
  isInline?: boolean;
  text: string;
};

const Assistant = ({ isInline, text }: AssistantProps): ReactElement => {
  const [isMounted, setIsMounted] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);

  const textRef = useRef<HTMLParagraphElement>(null);

  function animateGeneration(textString: string, index: number): void {
    if (textRef.current) {
      textRef.current.innerHTML = textString.substring(0, index);
      let newIndex = index;
      if (newIndex < textString.length) {
        let pause;
        if (textString[newIndex] === '<') {
          const closingTagIndex = textString.indexOf('>', newIndex);
          if (closingTagIndex !== -1) {
            pause = 0;
            newIndex = closingTagIndex + 1;
          }
        } else if (textString[newIndex] === '/') {
          const closingTagIndex = textString.indexOf('>', newIndex);
          if (closingTagIndex !== -1) {
            pause = 0;
            newIndex = closingTagIndex + 1;
          }
        } else if (textString[newIndex] === ',' || textString[newIndex] === '.') {
          pause = 100;
        } else {
          pause = 30;
        }
        setTimeout(() => {
          animateGeneration(textString, newIndex + 1);
        }, pause);
      }
    }
  }

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isMounted) {
      animateGeneration(text, 0);
    }
  }, [isMounted, text]);

  return (
    <div className={isInline ? s.inlineWrapper : s.wrapper}>
      <m.div
        initial={isInline ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }}
        animate={isCollapsed ? { opacity: 0, scale: 0 } : { opacity: 1, scale: 1 }}
        className={isInline ? s.inlineWrapperBubble : s.wrapperBubble}
      >
        <p>
          <strong>Glade Ai Assistant{isInline ? ':' : ''}</strong>
        </p>
        {isMounted && <p className='body-sm' ref={textRef} dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(text) }} />}
      </m.div>
      <m.button
        onClick={() => {
          if (!isInline) {
            setIsCollapsed(!isCollapsed);
          }
        }}
        initial={{ scale: 0 }}
        animate={{ scale: 1 }}
        transition={{ duration: 0.5, type: 'spring' }}
        className={isInline ? s.inlineWrapperAI : s.wrapperAI}
        disabled={isInline}
      >
        <NoodleAI />
      </m.button>
    </div>
  );
};

export default Assistant;
